import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import NavCard from 'tio-ui/components/nav-card';
import SectionHeader from 'tio-ui/components/section-header';
import SectionPanel from 'tio-ui/components/section-panel';
import {
  PhArrowLeft,
  PhBriefcase,
  PhGraduationCap,
  PhGridFour,
  PhLayout,
  PhLightning,
  PhMapTrifold,
  PhPiggyBank,
  PhRocket,
  PhStar,
  PhTarget,
  PhTrendUp,
} from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="tio-inline-row gap-2 tio-fg-brand dark:text-violet-400 hover:tio-fg-brand dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Layout & Navigation"
      @subtitle="PageHero, NavCard, SectionHeader, and SectionPanel for building page structures."
      @icon={{PhLayout}}
      @gradient="cool"
      @decoration="circles"
    >
      <:actions>
        <Chip @intent="primary">4 Components</Chip>
      </:actions>
    </PageHero>

    <div class="space-y-8">

      {{! ========================================
            PageHero
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhLayout @size={{20}} class="tio-fg-brand" />
            <span>PageHero</span>
            <Chip @size="sm" @intent="primary">6 Gradients</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Gradient hero banner with two modes:
            <strong>block mode</strong>
            (icon/badge/footer blocks) and
            <strong>convenience mode</strong>
            (@title/@subtitle/@icon with stat/actions/content blocks). 11 theme presets, 4 SVG
            decoration patterns, customizable rounding.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4 space-y-4">
            {{! Convenience mode demo }}
            <Text @size="sm" @weight="semibold" class="tio-fg-secondary">Convenience Mode:</Text>
            <PageHero
              @title="Section 127 Optimizer"
              @subtitle="Maximize your tax-free education benefit"
              @icon={{PhPiggyBank}}
              @gradient="cool"
              @decoration="circles"
            >
              <:stat>
                <div class="tio-text-3xl-bold">$5,250</div>
                <div class="text-sm text-white/70">Annual Limit</div>
              </:stat>
              <:actions>
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @size="sm"
                  class="tio-fg-inverse border-white/40 hover:bg-white/10"
                >
                  Learn More
                </Button>
              </:actions>
            </PageHero>

            {{! Block mode demo }}
            <Text @size="sm" @weight="semibold" class="tio-fg-secondary">Block Mode:</Text>
            <PageHero @gradient="brand" @decoration="circles">
              <:icon>
                <PhGraduationCap @size={{32}} />
                <Heading @level={{2}} class="tio-fg-inverse">Brand + Circles</Heading>
              </:icon>
              <:badge>
                <Chip @intent="primary">Badge Slot</Chip>
              </:badge>
              <:default>
                <Text class="text-white/80">Block mode with icon, badge, and footer blocks.</Text>
              </:default>
              <:footer>
                <Text @size="sm" class="text-white/70">Footer block with top border divider</Text>
              </:footer>
            </PageHero>

            {{! Preset grid — PageHero exposes 6 brand-leaning gradient presets.
                brand-soft renders dark text on a pale wash in light mode, so its
                swatch uses tio-hero-title (inherits --tio-hero-fg) instead of
                forcing white. }}
            <Text @size="sm" @weight="semibold" class="tio-fg-secondary">All 6 Gradient Presets:</Text>
            <div class="grid grid-cols-3 lg:grid-cols-3 gap-3">
              <PageHero @gradient="brand" @rounded="lg"><span
                  class="tio-fg-inverse tio-text-sm-semibold"
                >brand</span></PageHero>
              <PageHero @gradient="brand-soft" @rounded="lg"><span
                  class="tio-hero-title tio-text-sm-semibold"
                >brand-soft</span></PageHero>
              <PageHero @gradient="warm" @rounded="lg"><span
                  class="tio-fg-inverse tio-text-sm-semibold"
                >warm</span></PageHero>
              <PageHero @gradient="cool" @rounded="lg"><span
                  class="tio-fg-inverse tio-text-sm-semibold"
                >cool</span></PageHero>
              <PageHero @gradient="neutral" @rounded="lg"><span
                  class="tio-fg-inverse tio-text-sm-semibold"
                >neutral</span></PageHero>
              <PageHero @gradient="hero" @rounded="lg"><span
                  class="tio-fg-inverse tio-text-sm-semibold"
                >hero</span></PageHero>
            </div>
          </div>

          <details class="border rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer tio-text-sm-medium tio-fg-secondary tio-bg-sunken rounded-lg hover:tio-bg-sunken-hover"
            >
              Code Examples
            </summary>
            <pre class="p-4 text-xs tio-fg-secondary tio-bg-sunken overflow-x-auto border-t"><code
              >{{! Convenience mode }}
                &lt;PageHero @title="Section 127 Optimizer" @subtitle="Maximize your tax-free
                benefit" @icon=&#123;&#123;PhPiggyBank&#125;&#125; @gradient="cool"
                @decoration="circles"&gt; &lt;:stat&gt;&lt;div
                class="tio-text-3xl-bold"&gt;$5,250&lt;/div&gt;&lt;/:stat&gt;
                &lt;:actions&gt;&lt;Button&gt;Learn More&lt;/Button&gt;&lt;/:actions&gt;
                &lt;/PageHero&gt;

                {{! Block mode }}
                &lt;PageHero @gradient="brand" @decoration="circles"&gt;
                &lt;:icon&gt;&lt;PhGraduationCap @size=&#123;&#123;32&#125;&#125; /&gt; &lt;Heading
                @level=&#123;&#123;1&#125;&#125;
                class="tio-fg-inverse"&gt;Title&lt;/Heading&gt;&lt;/:icon&gt; &lt;:default&gt;Body
                content&lt;/:default&gt; &lt;:footer&gt;Footer content&lt;/:footer&gt;
                &lt;/PageHero&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            NavCard
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhMapTrifold @size={{20}} class="tio-fg-brand" />
            <span>NavCard</span>
            <Chip @size="sm" @intent="success">3 Layouts</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Unified navigation card with vertical, horizontal, and gradient layout variants.
            Consolidates the former QuickActionLink, ActionLinkCard, and ToolCard components.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4 space-y-4">
            <Text @size="sm" @weight="semibold" class="tio-fg-secondary">Vertical (default):</Text>
            <div class="grid grid-cols-3 gap-4">
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhRocket}}
                @title="Get Started"
                @subtitle="Begin your journey"
                @intent="brand"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhTarget}}
                @title="Explore"
                @subtitle="Find programs"
                @intent="brand"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhStar}}
                @title="Favorites"
                @subtitle="Saved items"
                @intent="warning"
              />
            </div>

            <Text @size="sm" @weight="semibold" class="tio-fg-secondary">Horizontal:</Text>
            <div class="space-y-2">
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhBriefcase}}
                @title="Career Explorer"
                @subtitle="Browse career paths"
                @layout="horizontal"
                @intent="info"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhGraduationCap}}
                @title="Programs"
                @subtitle="View eligible programs"
                @layout="horizontal"
                @intent="success"
              />
            </div>

            <Text @size="sm" @weight="semibold" class="tio-fg-secondary">Gradient:</Text>
            <div class="grid grid-cols-2 gap-4">
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhLightning}}
                @title="Skills Assessment"
                @subtitle="Evaluate your skills"
                @layout="gradient"
                @intent="brand"
              />
              <NavCard
                @route="authenticated.reimagined"
                @icon={{PhTrendUp}}
                @title="Career Growth"
                @subtitle="Track progress"
                @layout="gradient"
                @intent="brand"
              />
            </div>
          </div>

          <details class="border rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer tio-text-sm-medium tio-fg-secondary tio-bg-sunken rounded-lg hover:tio-bg-sunken-hover"
            >
              Code Examples
            </summary>
            <pre class="p-4 text-xs tio-fg-secondary tio-bg-sunken overflow-x-auto border-t"><code
              >&lt;NavCard @route="authenticated.reimagined" @icon=&#123;&#123;PhRocket&#125;&#125;
                @title="Get Started" @subtitle="Begin your journey" @intent="brand" /&gt;
                &lt;NavCard @route="authenticated.reimagined"
                @icon=&#123;&#123;PhBriefcase&#125;&#125; @title="Career Explorer"
                @layout="horizontal" @intent="info" /&gt; &lt;NavCard
                @route="authenticated.reimagined" @icon=&#123;&#123;PhLightning&#125;&#125;
                @title="Skills Assessment" @layout="gradient" @intent="brand" /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            SectionHeader + SectionPanel
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhGridFour @size={{20}} class="tio-fg-brand" />
            <span>SectionHeader &amp; SectionPanel</span>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            <strong>SectionHeader</strong>
            — icon + title heading with optional subtitle and trailing slot.
            <strong>SectionPanel</strong>
            — full card container with icon header and body.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4 space-y-4">
            <SectionHeader
              @icon={{PhStar}}
              @title="Section Header"
              @subtitle="With subtitle and trailing content"
            >
              <:trailing>
                <Chip @intent="primary" @size="sm">Badge</Chip>
              </:trailing>
            </SectionHeader>

            <SectionPanel @icon={{PhBriefcase}} @title="Section Panel" @iconColor="violet">
              <:trailing>
                <Button @intent="primary" @size="sm" @appearance="outlined">Action</Button>
              </:trailing>
              <:body>
                <Text @variant="muted">Panel body content goes here. This is a card container with a
                  styled header.</Text>
              </:body>
            </SectionPanel>
          </div>

          <details class="border rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer tio-text-sm-medium tio-fg-secondary tio-bg-sunken rounded-lg hover:tio-bg-sunken-hover"
            >
              Code Examples
            </summary>
            <pre class="p-4 text-xs tio-fg-secondary tio-bg-sunken overflow-x-auto border-t"><code
              >&lt;SectionHeader @icon=&#123;&#123;PhStar&#125;&#125; @title="My Section"
                @subtitle="Optional subtitle"&gt;
                &lt;:trailing&gt;&lt;Chip&gt;Badge&lt;/Chip&gt;&lt;/:trailing&gt;
                &lt;/SectionHeader&gt; &lt;SectionPanel @icon=&#123;&#123;PhBriefcase&#125;&#125;
                @title="Panel Title" @iconColor="violet"&gt; &lt;:body&gt;Content here&lt;/:body&gt;
                &lt;/SectionPanel&gt;</code></pre>
          </details>
        </:body>
      </Section>
    </div>
  </VStack>
</template>
